import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { formatDate } from '../../../utils/formatting'
import {
  DATE_COLUMN_WIDTH,
  ID_COLUMN_WIDTH,
  NAME_COLUMN_MAX_WIDTH,
} from '../../../components/DataTable/DataTable.constants'

import { AppLayoutContext } from '../../../providers/AppLayout'
import { useUrlStateParams } from '../../../utils/url'
import { SnackbarContext } from '../../../providers/SnackbarContext'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { TrailSteward } from '../../../models/TrailSteward.model'

import UserService from '../../../services/UserService/UserService'
import { TrailStewardService } from '../../../services/TrailStewardService/TrailStewardService'
import { TrailSystemService } from '../../../services/TrailSystemService/TrailSystemService'
import { TrailService } from '../../../services/TrailService/TrailService'

import {
  Box,
  FormControl,
  OutlinedInput,
  InputLabel,
  MenuItem,
  Stack,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Menu,
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DataTable from '../../../components/DataTable/DataTable.component'
import Loading from '../../../components/Loading/Loading.component'

import Link from '@mui/material/Link'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import { AppStateContext } from '../../../providers/AppStateContext'
import TrailStewardForm from '../shared/TrailSteward.component'
import { TrailSystemRelations } from '../../../models/TrailSystemRelations.model'
import ConfirmDeleteDialog from '../../../components/DeleteDialog/ConfirmDeleteDialog.component'

type TrailStewardSearchProps = {
  nested?: boolean
  systemId?: number
  trailId?: number
  hideHeader?: boolean
  excludeColumns?: string[]
}
const TrailStewardSearch = (props: TrailStewardSearchProps): JSX.Element => {
  const [currentSearchParameters] = useSearchParams()
  const {
    nested,
    systemId = Number(currentSearchParameters.get('trail-system')) || undefined,
    trailId = Number(currentSearchParameters.get('trail')) || undefined,
    hideHeader = !systemId && !trailId,
    excludeColumns = [],
  } = props

  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editTrailSteward])

  const [searchError, setSearchError] = useState('')
  const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<null | HTMLElement>(null)
  const isActionsMenuOpen = Boolean(rowActionsAnchorEl)
  const [selectedRow, setSelectedRow] = useState<TrailSteward | undefined>()
  const [isAdding, setIsAdding] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)
  const [name, setName] = useUrlStateParams(
    '',
    'name',
    (value) => encodeURIComponent(value.toString()),
    (value) => (value ? decodeURIComponent(value) : ''),
  )
  const nameInputRef = useRef<HTMLInputElement>()
  const [orderBy, setOrderBy] = useState<keyof TrailSteward | undefined>('id')
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc' | 'ASC' | 'DESC' | undefined>('DESC')
  const [page, setPage] = useUrlStateParams(
    1,
    'page',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 1),
  )
  const [pageSize, setPageSize] = useUrlStateParams(
    12,
    'page-size',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 12),
  )
  const currentRegionCode = AppStateContext.getRegion()

  useEffect(() => {
    if (!nested) {
      AppLayoutContext.setPageName('Trail Stewards')
    }
  }, [])

  const queryClient = useQueryClient()

  // fetch the trail for use in the add dialog
  const trailService = useMemo(() => new TrailService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isTrailLoading, data: trail } = useQuery(
    ['TrailService.get', { id: trailId, relations: [] }, AppStateContext.getRegion()],
    () => trailService.get({ id: trailId, relations: [] }),
    {
      enabled: !!trailId,
      onError: (err) => {
        console.error(err)
      },
    },
  )

  // fetch the trail system for use in the add dialog
  const trailSystemService = useMemo(() => new TrailSystemService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isTrailSystemLoading, data: trailSystem } = useQuery(
    [
      'TrailSystemService.get',
      { id: systemId, relations: [TrailSystemRelations.trailStewards] },
      AppStateContext.getRegion(),
    ],
    () => trailSystemService.get({ id: systemId, relations: [TrailSystemRelations.trailStewards] }),
    {
      enabled: !!systemId,
      onError: (err) => {
        console.error(err)
      },
    },
  )

  const trailStewardService = useMemo(() => new TrailStewardService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isLoadingSponsors, data: trailStewardResponse } = useQuery(
    [
      'TrailStewardService.search',
      {
        systemId,
        trailId,
        page,
        pageSize,
        name,
        orderBy,
        orderDirection,
        // email,
      },
      AppStateContext.getRegion(),
    ],
    () =>
      trailStewardService.search({
        page,
        pageSize,
        // email,
        name,
        orderBy,
        orderDirection: orderDirection?.toUpperCase() as 'ASC' | 'DESC',
      }),
    {
      onError: (err) => {
        setSearchError(typeof err === 'string' ? err : 'An error occurred while searching for sponsors.')
        console.error(err)
      },
    },
  )
  useEffect(() => {
    if (
      trailStewardResponse &&
      page > 1 &&
      (!trailStewardResponse.total || (page - 1) * pageSize >= trailStewardResponse.total)
    ) {
      setPage(1)
    }
  }, [trailStewardResponse])

  // consolidate the various loading states into one
  useEffect(() => {
    setIsLoading(isTrailLoading || isTrailSystemLoading || isLoadingSponsors)
  }, [isTrailLoading, isTrailSystemLoading, isLoadingSponsors])

  const handleRowActionsClick = (event: React.MouseEvent<HTMLButtonElement>, trailSteward: TrailSteward) => {
    setRowActionsAnchorEl(event.currentTarget)
    setSelectedRow(trailSteward)
  }
  const handleRowActionsClose = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
  }

  const columns = useMemo(() => {
    const columns: GridColDef<TrailSteward>[] = [
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButton
              id={`row-actions-button-${params.row.id}`}
              color="primary"
              aria-label="row actions"
              aria-controls={selectedRow?.id === params.row.id ? 'row-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={selectedRow?.id === params.row.id ? 'true' : undefined}
              onClick={(event) => handleRowActionsClick(event, params.row)}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        ),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: ID_COLUMN_WIDTH,
        renderCell: (params) => (
          <Link color="primary" aria-label="trail steward detail" href={`/trail-steward/${params.row.id}`}>
            {params.row.id}
          </Link>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        // takes the space left over by the other columns so the table fits its container instead of scrolling
        flex: 1,
        minWidth: 160,
        maxWidth: NAME_COLUMN_MAX_WIDTH,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 180,
        maxWidth: NAME_COLUMN_MAX_WIDTH,
      },
      {
        field: 'phone',
        headerName: 'Phone',
        width: 160,
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        width: DATE_COLUMN_WIDTH,
        renderCell: (params) => formatDate(params.row.createdAt),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        width: DATE_COLUMN_WIDTH,
        renderCell: (params) => formatDate(params.row.updatedAt),
      },
    ]
    // remove any columns specified to be removed in the excludeColumns prop
    if (excludeColumns) {
      return columns.filter((column) => !excludeColumns.includes(column.field as string))
    }
    return columns
  }, [excludeColumns])

  const handleSubmit = () => {
    setPage(1)
    setName(nameInputRef?.current?.value || '')
  }

  const handleAddClick = () => {
    setIsAdding(true)
  }

  const handleAddClose = () => {
    setIsAdding(false)
  }

  const handleEditClick = () => {
    setIsEditing(true)
  }

  const handleEditClose = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
    setIsEditing(false)
  }

  const handleDeleteClick = () => {
    setIsDeleteConfirmationOpen(true)
  }

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false)
    handleRowActionsClose()
  }

  const deleteTrailSteward = useMutation(
    (mutationParams: { id: number }) => {
      setIsSaving(true)
      return trailStewardService.delete(mutationParams.id)
    },
    {
      onSuccess: () => {
        SnackbarContext.show('Trail Steward deleted successfully!')
        setRowActionsAnchorEl(null)
        setSelectedRow(undefined)
        // invalidate all queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSaving(false)
      },
      onError: (err) => {
        SnackbarContext.show(`Trail Steward failed to delete: ${err}`, 'error')
        console.error(err)
        setIsSaving(false)
      },
    },
  )

  const handleDelete = () => {
    if (selectedRow?.id) {
      setIsDeleteConfirmationOpen(false)
      deleteTrailSteward.mutate({ id: selectedRow.id })
    }
  }

  return (
    <>
      {selectedRow && (
        <FormDialog<TrailSteward>
          open={isEditing}
          entity={selectedRow}
          entityLabel="Trail Steward"
          onClose={handleEditClose}
        >
          <TrailStewardForm trailSteward={selectedRow} onEdit={handleEditClose} onCancel={handleEditClose} />
        </FormDialog>
      )}
      <FormDialog<TrailSteward> open={isAdding} entityLabel="Trail Steward" onClose={handleAddClose}>
        <TrailStewardForm onAdd={handleAddClose} onCancel={handleAddClose} />
      </FormDialog>
      <ConfirmDeleteDialog
        open={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onDelete={handleDelete}
        title="Confirm Delete"
        content={`Are you sure you want to delete trail steward id ${selectedRow?.id}: ${selectedRow?.name}?`}
      />
      <Paper sx={{ p: 2 }}>
        {!hideHeader && (
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            {trailSystem?.name ? `${trailSystem.name} ` : trail?.name ? `${trail.name} ` : ''}Trail Stewards
          </Typography>
        )}
        {(isLoading || isSaving) && !searchError && <Loading sx={{ py: 20 }} />}
        {searchError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{searchError}</Typography>
          </Stack>
        )}
        {!isLoading && !isSaving && !searchError && (
          <>
            <Box sx={{ pt: 1, mb: 1 }}>
              <Stack direction={{ zero: 'column', md: 'row' }} spacing={1}>
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel>Name</InputLabel>
                  <OutlinedInput
                    id="search-name"
                    label="Name"
                    defaultValue={name || ''}
                    inputRef={nameInputRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit()
                      }
                    }}
                    endAdornment={
                      nameInputRef?.current?.value ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="clear search name"
                            onClick={() => {
                              if (nameInputRef?.current?.value) {
                                nameInputRef.current.value = ''
                              }
                              setPage(1)
                              setName('')
                            }}
                            edge="end"
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined
                    }
                  />
                </FormControl>

                <Button
                  variant="outlined"
                  onClick={handleSubmit}
                  sx={(theme) => ({ [theme.breakpoints.up('md')]: { width: 150 }, maxWidth: '100%' })}
                >
                  Search
                </Button>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {hasWritePermissions && (!systemId || (systemId && trailSystem)) && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      color="primary"
                      aria-label="add new"
                      onClick={handleAddClick}
                    >
                      Add
                    </Button>
                  )}
                </Stack>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    if (nameInputRef?.current?.value) {
                      nameInputRef.current.value = ''
                    }
                    setPage(1)
                    setName('')
                  }}
                >
                  Clear
                </Button>
              </Stack>
            </Box>

            {trailStewardResponse?.data && trailStewardResponse.data.length > 0 ? (
              <>
                <Menu
                  id="row-actions-menu"
                  anchorEl={rowActionsAnchorEl}
                  open={isActionsMenuOpen}
                  onClose={handleRowActionsClose}
                  MenuListProps={{
                    'aria-labelledby': `row-actions-button${selectedRow?.id}`,
                  }}
                >
                  <MenuItem href={`/trail-steward/${selectedRow?.id}`} component={Link}>
                    View
                  </MenuItem>
                  {hasWritePermissions && <MenuItem onClick={handleEditClick}>Edit</MenuItem>}
                  {hasWritePermissions && <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>}
                </Menu>
                <DataTable
                  columns={columns}
                  data={trailStewardResponse.data}
                  total={trailStewardResponse.total}
                  paginationMode="server"
                  page={page}
                  pageSize={pageSize}
                  onPageChange={(newPage) => {
                    setPage(newPage)
                  }}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize)
                  }}
                  orderBy={orderBy}
                  orderDirection={orderDirection?.toLocaleLowerCase() as 'asc' | 'desc'}
                  onOrderChange={(newOrder) => {
                    setOrderBy(newOrder.field ? (newOrder.field as keyof TrailSteward) : undefined)
                    setOrderDirection(newOrder.sort)
                  }}
                />
              </>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography>No results found</Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default TrailStewardSearch
